// Shared wire protocol between the world server and the Phaser client.
//
// SINGLE SOURCE OF TRUTH. This file lives inside the client tree so the Vite/PWA
// build resolves it with zero alias config; the server imports it relatively
// (see server/tsconfig.json `include`). Keep it dependency-free (no Phaser, no
// node) so both sides can import it.

export const PROTOCOL_VERSION = 1

export type Tile = { x: number; y: number }
export type Facing = 'up' | 'down' | 'left' | 'right'
export type AgentId = string
/** A location id === a client SceneName value === a `homeLocation` in characters.json. */
export type LocationId = string

export type Moods = {
    faim: number
    energie: number
    social: number
    fun: number
    notes: number
}
export type MoodKey = keyof Moods

// ── Avatar appearance (admin-personalized) ───────────────────────────────────
export type Gender = 'fille' | 'garcon' | 'neutre'
export type HairColor = 'brun' | 'chatain' | 'blond' | 'roux' | 'noir' | 'colore'
export type SkinColor = 'clair' | 'moyen' | 'mat' | 'fonce'
export type ShirtColor = 'rouge' | 'bleu' | 'vert' | 'jaune' | 'violet' | 'rose' | 'gris' | 'orange'

export type Appearance = { gender: Gender; hairColor: HairColor; skinColor: SkinColor; shirtColor: ShirtColor }

/** hex (no '#') — used for DiceBear options AND, for the shirt, the world-sprite tint. */
export const HAIR_HEX: Record<HairColor, string> = { brun: '4a2f1b', chatain: '8b5a2b', blond: 'e8c87a', roux: 'b5562a', noir: '1c1410', colore: '7c5cff' }
export const SKIN_HEX: Record<SkinColor, string> = { clair: 'f2d3b1', moyen: 'e0ac86', mat: 'c68642', fonce: '8d5524' }
export const SHIRT_HEX: Record<ShirtColor, string> = { rouge: 'e53e3e', bleu: '3182ce', vert: '38a169', jaune: 'd69e2e', violet: '805ad5', rose: 'd53f8c', gris: '718096', orange: 'dd6b20' }

export const DEFAULT_APPEARANCE: Appearance = { gender: 'neutre', hairColor: 'chatain', skinColor: 'moyen', shirtColor: 'vert' }
export const APPEARANCE_PALETTE = {
    gender: ['fille', 'garcon', 'neutre'] as Gender[],
    hairColor: Object.keys(HAIR_HEX) as HairColor[],
    skinColor: Object.keys(SKIN_HEX) as SkinColor[],
    shirtColor: Object.keys(SHIRT_HEX) as ShirtColor[],
}
/** Phaser tint number (0xRRGGBB) for the world sprite, from the shirt color. */
export function shirtTintNumber(c: ShirtColor): number { return parseInt(SHIRT_HEX[c] || 'ffffff', 16) }

function hashId(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) } return h >>> 0 }
/** Deterministic, varied appearance for an UNpersonalized agent → the class looks diverse by default. */
export function defaultAppearanceFor(id: string): Appearance {
    const h = hashId(id)
    const pick = <T>(arr: T[], salt: number): T => arr[(Math.imul(h ^ salt, 2654435761) >>> 0) % arr.length]
    return {
        gender: pick(APPEARANCE_PALETTE.gender, 0x9e3779b1),
        hairColor: pick(APPEARANCE_PALETTE.hairColor, 0x85ebca77),
        skinColor: pick(APPEARANCE_PALETTE.skinColor, 0xc2b2ae3d),
        shirtColor: pick(APPEARANCE_PALETTE.shirtColor, 0x27d4eb2f),
    }
}

// avataaars hairstyle sets so `gender` actually reads as feminine/masculine.
const FEM_TOPS = ['bob', 'bun', 'curly', 'curvy', 'longButNotTooLong', 'miaWallace', 'straight01', 'straight02', 'straightAndStrand', 'bigHair', 'frida']
const MASC_TOPS = ['shortFlat', 'shortRound', 'shortWaved', 'shortCurly', 'theCaesar', 'theCaesarAndSidePart', 'frizzle', 'sides', 'dreads01', 'shaggy']

/** One mapping (shared by the in-game DiceBear AND the editor preview) → avataaars options. */
export function appearanceToAvataaarsParams(a: Appearance, seed: string) {
    const top = a.gender === 'fille' ? FEM_TOPS : a.gender === 'garcon' ? MASC_TOPS : [...FEM_TOPS, ...MASC_TOPS]
    return {
        seed: `${seed}-${a.gender}`,
        top,
        hairColor: [HAIR_HEX[a.hairColor]],
        skinColor: [SKIN_HEX[a.skinColor]],
        clothesColor: [SHIRT_HEX[a.shirtColor]],
        facialHairProbability: 0, // collégiens
    }
}

/** The bounded action set the LLM may choose from (also the safety enforcement point). */
export type ActionKind = 'go_to' | 'talk_to' | 'use_object' | 'propose_event' | 'wait' | 'create_object'

export type AgentKind = 'student' | 'staff'

/** A weighted social tie, coarse-labelled by familiarity. */
export type RelationshipType = 'inconnu' | 'connaissance' | 'ami' | 'proche'
/** One relationship surfaced to the client (Kazi card "Amis avec…"). Pseudonymous, fiction-only. */
export type RelationshipBrief = { id: AgentId; displayName: string; familiarity: number; type: RelationshipType }

/** Everything the client needs to render an agent + populate its live Kazi card. */
export type AgentPublicState = {
    id: AgentId
    displayName: string
    kind: AgentKind
    location: LocationId
    tile: Tile
    facing: Facing
    /** Human-readable current activity, e.g. "se rend en cours de Maths". */
    action: string
    /** Short-term plan / what they intend to do next. */
    intent: string
    /** Why they're doing it (from the last decision). */
    reason: string
    moods: Moods
    /** DiceBear seed/style — face shown on the Kazi card. */
    avatarSeed: string
    avatarStyle: string
    /** Owner has claimed this avatar (P3). */
    claimed: boolean
    /** displayName is a real admin-set name (vs the pseudonymous "Élève NN") → show a nameplate. */
    named: boolean
    /** Admin-personalized look (drives the world tint + the DiceBear card); undefined = default. */
    appearance?: Appearance
    /** Approved photo→pixel-art portrait (server path, e.g. "/avatars/<id>.png"); overrides the DiceBear face on the card. */
    portrait?: string
    /** Strongest known relationships (pseudonymous, fiction-only) — drives the "Amis avec…" card line. */
    relationships?: RelationshipBrief[]
    /** Coarse current feeling derived from the moods (e.g. "fatigué·e") — colours the card + tone. */
    emotion?: string
    /** Evolving self-concept (one first-person sentence, re-synthesised ~daily from reflections). */
    selfConcept?: string
    /** Today's light intention ("aujourd'hui je veux…"). */
    agenda?: string
    /** "Côte de popularité" 0..100 — connectedness + social buzz (token-free). */
    aura?: number
    /** props this agent created + how many times others used them */
    creations?: { label: string; uses: number }[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Envelope
// ─────────────────────────────────────────────────────────────────────────────

export type Envelope<T extends string, P> = {
    /** protocol version */
    v: number
    /** message type */
    t: T
    /** monotonic sequence (server-assigned for S2C, client-assigned for C2S) */
    seq: number
    /** wall-clock ms at send time */
    ts: number
    /** payload */
    p: P
}

// ─────────────────────────────────────────────────────────────────────────────
// Server → Client
// ─────────────────────────────────────────────────────────────────────────────

export type SnapshotMsg = Envelope<'snapshot', {
    location: LocationId
    /** game-epoch ms (deferred Paris instant; see ClockMsg.simEpochMs) — unused by the client */
    simTime: number
    agents: AgentPublicState[]
    /** dynamic agent-created props currently in this location */
    objects: DynamicObjectPublic[]
    /** the agent this client is embodying, if any */
    you?: { agentId: AgentId; tile: Tile }
}>

/** Tell the client to pathfind an agent to a tile (client calls gridEngine.moveTo). */
export type AgentMoveMsg = Envelope<'agent-move', {
    agentId: AgentId
    location: LocationId
    target: Tile
}>

/** Hard placement (cross-scene arrival / desync reconciliation). */
export type AgentTeleportMsg = Envelope<'agent-teleport', {
    agentId: AgentId
    location: LocationId
    tile: Tile
    facing?: Facing
}>

/** A talk / use / propose action surfaced to the client (bubble + beat + prop toast). */
export type AgentActionMsg = Envelope<'agent-action', {
    agentId: AgentId
    location: LocationId
    kind: 'talk' | 'use' | 'propose'
    line?: string
    targetAgentId?: AgentId
    objectId?: string
    eventId?: string
    /** for kind:'use' — the prop tile + label + mood deltas, so the client shows the same toast as the player */
    tile?: Tile
    label?: string
    effects?: Partial<Record<MoodKey, number>>
}>

/** A prop an agent created at runtime, as the client needs to render + use it. */
export type DynamicObjectPublic = {
    objectId: string
    location: LocationId
    tile: Tile
    /** templated TYPE label (fallback) — `name` is what the creator authored and what the UI shows */
    label: string
    /** creator-authored display name ("Le coin BD de Sara"); falls back to `label` if absent/empty */
    name?: string
    /** creator-authored one-liner about what it's for (screened) — shown on the prop card */
    blurb?: string
    /** rlsheet (Kenney roguelike) frame index to draw as the overlay sprite */
    spriteFrame: number
    solid: boolean
    effects: Partial<Record<MoodKey, number>>
    cooldownSimMin: number
    creatorId: AgentId
    creatorName: string
    /** classmates who later improved/decorated it (co-authors) — display names */
    coCreators?: string[]
    /** evolution tier (0 = fresh). Rises as the class uses it; drives decor + honorific + a small bump. */
    level?: number
    /** extra rlsheet frame drawn ON TOP once leveled up (e.g. a banner) — purely cosmetic */
    decorFrame?: number
    /** earned title once it becomes a class favourite ("⭐ Le QG de la 5eA") */
    honorific?: string
    /** PURE DECORATION: no effect / not "usable" — built to personalise the space (plant, poster, banner…) */
    decorative?: boolean
}

/** A new agent-created prop appeared in a location. */
export type WorldObjectSpawnMsg = Envelope<'world-object-spawn', {
    object: DynamicObjectPublic
    /** show a small "X a créé …" flourish when first placed */
    announce?: boolean
}>

/** An agent-created prop was removed (decayed / admin-cleared). */
export type WorldObjectRemoveMsg = Envelope<'world-object-remove', {
    objectId: string
    location: LocationId
}>

/** Live state for the Kazi card (reply to query-state, or pushed on change). */
export type AgentStateMsg = Envelope<'agent-state', { agent: AgentPublicState }>

/** An emergent social beat (e.g. someone organised a goûter). */
export type EventBeatMsg = Envelope<'event-beat', {
    id: string
    title: string
    location: LocationId
    participants: AgentId[]
    description: string
}>

/** A single Journal log entry — a notable action/event, attributed + game-time-stamped. */
export type LogKind = 'talk' | 'use' | 'propose' | 'relationship' | 'create' | 'school' | 'evolve'
export type LogEntry = {
    /** game-epoch ms (deferred Paris instant) when it happened */
    atSim: number
    /** rendered game time, e.g. "Mercredi 09:42" */
    timeLabel: string
    kind: LogKind
    /** who did it (displayName) */
    actorName: string
    location: LocationId
    /** human-readable line, e.g. "Naïma à Hugo : « … »" */
    text: string
}
/** Sent once on connect — the recent log ring. */
export type LogHistoryMsg = Envelope<'log-history', { entries: LogEntry[] }>
/** Streamed to all sessions whenever a new entry is pushed. */
export type LogEntryMsg = Envelope<'log-entry', { entry: LogEntry }>

/** Reply to query-conversation: the stored player↔agent thread, oldest→newest. */
export type ConversationHistoryMsg = Envelope<'conversation-history', {
    agentId: AgentId
    turns: { sender: 'player' | 'agent'; text: string; createdAt: number }[]
}>

/** Running token-spend meter. */
export type CostMeterMsg = Envelope<'cost-meter', {
    /** USD spent in the current in-game day */
    gameDayUsd: number
    /** USD spent since server start */
    totalUsd: number
    /** USD by model id */
    byModel: Record<string, number>
    /** number of LLM "thinks" so far */
    thinks: number
    /** whether the server is running the real model or the deterministic mock */
    mode?: 'mock' | 'live'
}>

/** Deferred game clock — broadcast on an interval and once on connect. dayName/hhmm are server-rendered. */
export type ClockMsg = Envelope<'clock', {
    /** weekday name, e.g. "lundi" (French; one of the 7 days). */
    dayName: string
    /** "HH:MM" game time-of-day. */
    hhmm: string
    /** authoritative game-epoch ms (the deferred Paris instant). */
    simEpochMs: number
    /** game-epoch ms when the collège next opens (next weekday 08:00, vacances-aware).
     *  null/absent = unknown (forced holiday / no calendar end date) → hide the countdown. */
    nextSchoolOpenMs?: number | null
}>

export type ErrorMsg = Envelope<'error', { code: string; message: string }>

/** One agent's position for the whole-school minimap (positions only — tiny, no moods/LLM). */
export type PulseAgent = {
    id: AgentId
    location: LocationId
    tile: Tile
    kind: AgentKind | 'player'
    /** shirt colour (students) → dot colour, so you can spot your avatar */
    shirt?: ShirtColor
    /** has an admin-set real name (brighter dot) */
    named: boolean
}

/** All agents' positions across EVERY location, broadcast to all clients ~1/s (drives the minimap). */
export type WorldPulseMsg = Envelope<'world-pulse', { agents: PulseAgent[] }>

// ─────────────────────────────────────────────────────────────────────────────
// World facts — real-world data feeds (weather / school calendar / football).
// Fetched server-side, injected into EVERY agent inference (shared reality), and
// pushed to clients to drive the weather widget + holiday state. Keep dependency-free.
// ─────────────────────────────────────────────────────────────────────────────

/** Real weather in Grisolles (Open-Meteo, keyless). */
export type WeatherFacts = {
    tempC: number
    /** WMO weather code (drives the widget glyph). */
    code: number
    /** FR label, e.g. "ensoleillé", "pluie faible", "canicule". */
    label: string
    raining: boolean
    /** ≥ ~32°C → canicule. */
    hot: boolean
    /** ≤ ~2°C. */
    cold: boolean
}

/** Real French school calendar (Zone C / académie de Toulouse) + jours fériés. */
export type CalendarFacts = {
    onHoliday: boolean
    /** set when onHoliday — e.g. "Vacances de la Toussaint". */
    holidayName?: string
    /** set when onHoliday — real ISO end of the current vacances (drives the reopen countdown). */
    holidayEndISO?: string
    nextHolidayName?: string
    /** real ISO bounds of the NEXT vacances (so the countdown skips over them, e.g. Friday eve before). */
    nextHolidayStartISO?: string
    nextHolidayEndISO?: string
    /** drives "plus que X jours avant les vacances…". */
    daysUntilNextHoliday?: number
    /** jour férié name if today is one. */
    ferieToday?: string
}

/** Toulouse FC real results (football-data.org, key-gated). */
export type FootballFacts = {
    lastMatch?: { opponent: string; score: string; won: boolean | null; dateLabel: string }
    nextMatch?: { opponent: string; dateLabel: string; home: boolean }
}

/** The shared real-world snapshot — any field may be absent if its source is unavailable. */
export type WorldFacts = {
    weather?: WeatherFacts
    calendar?: CalendarFacts
    football?: FootballFacts
    /** wall-clock ms of the last successful refresh (0 = never). */
    updatedAt: number
}

/** Pushed on connect + on every refresh — drives the weather widget and holiday chip. */
export type WorldFactsMsg = Envelope<'world-facts', { facts: WorldFacts }>

/** Sent to an avatar's OWNER (their live sessions) the moment a fresh daily Récap is generated — an
 *  in-app nudge to go read it. The away-from-app pull-back is a Web Push notification (server-side). */
export type RecapReadyMsg = Envelope<'recap-ready', { avatarId: AgentId; headline: string }>

// ── Moments (the interactive owner loop) ──────────────────────────────────────
/** One tappable choice in a Moment. */
export type MomentOption = { id: string; label: string; emoji?: string }
/** S2C: the owner's avatar hit a junction → a bounded, deadlined choice. Owner-targeted (not broadcast). */
export type MomentOfferMsg = Envelope<'moment-offer', {
    momentId: string
    avatarId: AgentId
    title: string
    prompt: string
    options: MomentOption[]
    expiresAtMs: number
}>
/** S2C: the outcome of an answered (or expired) Moment + the rewards earned. */
export type MomentResultMsg = Envelope<'moment-result', {
    momentId: string
    outcomeText: string
    rewards: { xp: number; auraDelta?: number; levelUp?: boolean }
}>
/** S2C: the owner's progression changed (level bar). */
export type ProgressUpdateMsg = Envelope<'progress-update', {
    avatarId: AgentId
    xp: number
    level: number
    nextAt: number
    pct: number
}>

export type ServerMessage =
    | SnapshotMsg
    | AgentMoveMsg
    | AgentTeleportMsg
    | AgentActionMsg
    | AgentStateMsg
    | EventBeatMsg
    | CostMeterMsg
    | ClockMsg
    | LogHistoryMsg
    | LogEntryMsg
    | ConversationHistoryMsg
    | WorldPulseMsg
    | WorldFactsMsg
    | RecapReadyMsg
    | WorldObjectSpawnMsg
    | WorldObjectRemoveMsg
    | MomentOfferMsg
    | MomentResultMsg
    | ProgressUpdateMsg
    | ErrorMsg

export type ServerMessageType = ServerMessage['t']

// ─────────────────────────────────────────────────────────────────────────────
// Client → Server
// ─────────────────────────────────────────────────────────────────────────────

export type HelloMsg = Envelope<'hello', {
    token?: string
    /** which agent to embody (Phase 1: a created/observer student) */
    embodyAgentId?: AgentId
    displayName?: string
    /** admin "Régie": a pure listener — no body, no room observed (global broadcasts only). */
    spectate?: boolean
    /** companion app: an OWNER listener — sets the user, no body/room, receives owner-targeted Moments. */
    owner?: boolean
}>

/** C2S: the owner answered a Moment. */
export type MomentChooseMsg = Envelope<'moment-choose', { momentId: string; optionId: string }>

export type EnterLocationMsg = Envelope<'enter-location', { location: LocationId }>

/** Player is authoritative over its own body; report its grid position. */
export type PlayerMoveMsg = Envelope<'player-move', {
    location: LocationId
    tile: Tile
    facing?: Facing
}>

export type InteractMsg = Envelope<'interact', { targetAgentId: AgentId }>

/** Player sends a line to an agent (quick-reply or free text) → a multi-turn conversation. */
export type PlayerSayMsg = Envelope<'player-say', { targetAgentId: AgentId; text: string; quick?: boolean }>

/** Ask the server for the persisted conversation thread with an agent (seeds the messenger on open). */
export type QueryConversationMsg = Envelope<'query-conversation', { agentId: AgentId }>

export type QueryStateMsg = Envelope<'query-state', { agentId: AgentId }>

/** Owner steering (P3 — admin-gated). */
export type SetIntentMsg = Envelope<'set-intent', { agentId: AgentId; intent: string }>

/** Dev-only clock control for QA fast-forward. */
export type DevTimeMsg = Envelope<'dev-time', { multiplier: number }>

export type ClientMessage =
    | HelloMsg
    | EnterLocationMsg
    | PlayerMoveMsg
    | InteractMsg
    | PlayerSayMsg
    | QueryConversationMsg
    | QueryStateMsg
    | SetIntentMsg
    | DevTimeMsg
    | MomentChooseMsg

export type ClientMessageType = ClientMessage['t']

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (tiny, shared)
// ─────────────────────────────────────────────────────────────────────────────

export function decode(raw: string): Envelope<string, unknown> | null {
    try {
        const m = JSON.parse(raw)
        if (m && typeof m.t === 'string' && typeof m.seq === 'number') return m
        return null
    } catch {
        return null
    }
}
